/**
 * FiveFoldFriendsWidget
 * Displays Pañca-vargīya Maitri (Five-Fold Friendship) matrix
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/joy';
import { calculateFiveFoldFriendships, getFriendshipMatrix } from '../../utils/fiveFoldFriendship';
import type { PlanetName } from '../../utils/astroEngine';
import type { FriendshipType } from '../../utils/fiveFoldFriendship';

const CORE_PLANETS: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

const PLANET_ABBR: Record<PlanetName, string> = {
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke'
};

const FRIENDSHIP_COLORS: Record<FriendshipType, string> = {
  great_friend: '#15803d',  // Dark green
  friend: '#22c55e',        // Green
  neutral: '#94a3b8',       // Gray
  enemy: '#f97316',         // Orange
  great_enemy: '#dc2626'    // Red
};

const FRIENDSHIP_LABELS: Record<FriendshipType, string> = {
  great_friend: 'Great Friend',
  friend: 'Friend',
  neutral: 'Neutral',
  enemy: 'Enemy',
  great_enemy: 'Great Enemy'
};

const FRIENDSHIP_SYMBOLS: Record<FriendshipType, string> = {
  great_friend: '++',
  friend: '+',
  neutral: '=',
  enemy: '-',
  great_enemy: '--'
};

interface FiveFoldFriendsWidgetProps {
  placements?: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
}

export default function FiveFoldFriendsWidget({ placements }: FiveFoldFriendsWidgetProps) {
  if (!placements) {
    return (
      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
        No planetary data available
      </Typography>
    );
  }

  const friendships = calculateFiveFoldFriendships(placements);
  const matrix = getFriendshipMatrix(friendships);

  return (
    <Box>
      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
        Personalized planetary relationships based on natural and positional friendships
      </Typography>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', fontSize: '11px' }}>
        {Object.entries(FRIENDSHIP_SYMBOLS).map(([type, symbol]) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 'xs',
                bgcolor: FRIENDSHIP_COLORS[type as FriendshipType],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 600,
                fontSize: '10px'
              }}
            >
              {symbol}
            </Box>
            <Typography level="body-xs">{FRIENDSHIP_LABELS[type as FriendshipType]}</Typography>
          </Box>
        ))}
      </Box>

      {/* Friendship Matrix */}
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--joy-palette-text-secondary)' }}>
                From → To
              </th>
              {CORE_PLANETS.map(planet => (
                <th key={planet} style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 600 }}>
                  {PLANET_ABBR[planet]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CORE_PLANETS.map(fromPlanet => (
              <tr key={fromPlanet}>
                <td style={{ padding: '8px', fontWeight: 600, fontSize: '11px' }}>
                  {PLANET_ABBR[fromPlanet]}
                </td>
                {CORE_PLANETS.map(toPlanet => {
                  if (fromPlanet === toPlanet) {
                    return (
                      <td key={toPlanet} style={{ padding: '4px', textAlign: 'center' }}>
                        <Box sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: 'neutral.100', 
                          borderRadius: 'xs',
                          display: 'inline-block'
                        }} />
                      </td>
                    );
                  }

                  const friendship = matrix[fromPlanet]?.[toPlanet];
                  if (!friendship) {
                    return (
                      <td key={toPlanet} style={{ padding: '4px', textAlign: 'center' }}>
                        <Box sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: 'neutral.50', 
                          borderRadius: 'xs',
                          display: 'inline-block'
                        }} />
                      </td>
                    );
                  }

                  const color = FRIENDSHIP_COLORS[friendship.panchadha];
                  const symbol = FRIENDSHIP_SYMBOLS[friendship.panchadha];
                  const label = FRIENDSHIP_LABELS[friendship.panchadha];

                  return (
                    <td key={toPlanet} style={{ padding: '4px', textAlign: 'center' }}>
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'common.white' }}>
                              {fromPlanet} → {toPlanet}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'common.white' }}>
                              Natural: {FRIENDSHIP_LABELS[friendship.naisargika]}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'common.white' }}>
                              Positional: {FRIENDSHIP_LABELS[friendship.tatkalik]}
                            </Typography>
                            <Typography level="body-xs" sx={{ fontWeight: 600, mt: 0.5, color: 'common.white' }}>
                              Combined: {label}
                            </Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                        variant="solid"
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: color,
                            borderRadius: 'xs',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '11px',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {symbol}
                        </Box>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Typography level="body-xs" sx={{ mt: 2, color: 'text.tertiary', fontStyle: 'italic' }}>
        Note: Five-fold friendship combines natural (permanent) and positional (temporary) relationships for a personalized analysis.
      </Typography>
    </Box>
  );
}
